import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { homepageScenes } from "./scenes";
import type { MascotController } from "./runtime";

let runtimePromise: Promise<typeof import("./runtime.js")> | null = null;
function loadRuntime() {
  if (!runtimePromise)
    runtimePromise = import("./runtime.js").catch((error) => {
      runtimePromise = null;
      throw error;
    });
  return runtimePromise;
}

export function HomepageMascot({
  pageRef,
}: {
  pageRef: React.RefObject<HTMLElement | null>;
}) {
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    let controller: MascotController | null = null;
    let observer: MutationObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let overlayActive = false;
    const header = document.querySelector<HTMLElement>("[data-site-header]");
    const home = header?.querySelector<HTMLElement>("[data-mascot-home]");
    const scrollContainer = pageRef.current?.closest<HTMLElement>(
      "[data-site-scroll]"
    );
    const chat = pageRef.current?.querySelector<HTMLElement>(
      "[data-chat-expanded]"
    );
    const sky = pageRef.current?.querySelector<HTMLElement>(
      "#home-sky-discovery"
    );
    if (!header || !home || !scrollContainer || !pageRef.current) return;

    const sync = () => {
      if (!controller || cancelled || !pageRef.current?.isConnected) return;
      // Only automatic obstruction handling; no user hiding/motion preference.
      const overlayChat =
        chat?.dataset.chatLayout === "mobile" ||
        chat?.dataset.chatLayout === "docked" ||
        chat?.dataset.chatLayout === "fullscreen";
      overlayActive =
        header.dataset.menuOpen === "true" ||
        Boolean(overlayChat && chat?.dataset.chatExpanded === "true");
      // Keep the logo visible at the top, including while the chat is expanded.
      const blocked =
        chat?.dataset.chatLayout === "fullscreen" ||
        (overlayActive && scrollContainer.scrollTop > 2);
      if (blocked && !controller.host.hidden) controller.hide();
      if (!blocked && controller.host.hidden) controller.show();
      controller.host.dataset.skyGuests =
        sky?.dataset.skyVisitorsPhase || "waiting";
      controller.refresh();
    };
    const onScroll = () => {
      if (overlayActive) sync();
    };
    scrollContainer.addEventListener("scroll", onScroll, { passive: true });

    void loadRuntime()
      .then(({ mount }) => {
        if (cancelled || !pageRef.current?.isConnected) return;
        controller = mount({
          header,
          home,
          viewport: pageRef.current!,
          scrollContainer,
          scenes: homepageScenes,
          size: 176,
          mobileSize: 128,
          zIndex: 1100,
          onNavigate: (href) => {
            // Route changes remove scene/home anchors before passive effect
            // cleanup. Release animation/observers before those DOM nodes go.
            controller?.destroy();
            controller = null;
            navigate(href);
          },
          bottomInset: () => {
            if (
              !chat ||
              !["mobile", "docked"].includes(chat.dataset.chatLayout || "")
            )
              return 16;
            return Math.max(
              16,
              window.innerHeight - chat.getBoundingClientRect().top + 12
            );
          },
        });
        sync();
        observer = new MutationObserver(sync);
        observer.observe(header, {
          attributes: true,
          attributeFilter: ["data-menu-open"],
        });
        if (sky)
          observer.observe(sky, {
            attributes: true,
            attributeFilter: ["data-sky-visitors-phase"],
          });
        if (chat) {
          observer.observe(chat, {
            attributes: true,
            attributeFilter: ["data-chat-expanded", "data-chat-layout"],
          });
          resizeObserver = new ResizeObserver(sync);
          resizeObserver.observe(chat);
        }
        return controller.ready;
      })
      .then(() => {
        if (!cancelled) sync();
      })
      .catch((error) => {
        if (!cancelled)
          console.warn("Не удалось загрузить светлячка главной", error);
      });

    return () => {
      cancelled = true;
      observer?.disconnect();
      resizeObserver?.disconnect();
      scrollContainer.removeEventListener("scroll", onScroll);
      controller?.destroy();
    };
  }, [pageRef, navigate]);
  return null;
}
